import { useState, useEffect, useCallback } from 'react'
import { CheckCircle, Undo2 } from 'lucide-react'
import { useT } from '../../i18n/index.js'

// ── Types ──

export interface ExecutedAction {
  type: string
  payload?: Record<string, unknown>
}

interface UndoToastProps {
  voiceNoteId: string
  actionsExecuted: ExecutedAction[]
  undoWindowExpiresAt: string
  onUndo: (voiceNoteId: string) => void
  onExpire: () => void
}

// ── Component ──

export function UndoToast({ voiceNoteId, actionsExecuted, undoWindowExpiresAt, onUndo, onExpire }: UndoToastProps) {
  const t = useT()
  const [remainingSeconds, setRemainingSeconds] = useState(() => {
    return Math.max(0, Math.ceil((new Date(undoWindowExpiresAt).getTime() - Date.now()) / 1000))
  })

  useEffect(() => {
    if (remainingSeconds <= 0) {
      onExpire()
      return
    }

    const timer = setInterval(() => {
      setRemainingSeconds((prev) => {
        const next = Math.max(0, Math.ceil((new Date(undoWindowExpiresAt).getTime() - Date.now()) / 1000))
        if (next <= 0) {
          clearInterval(timer)
          onExpire()
          return 0
        }
        return next
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [undoWindowExpiresAt, onExpire, remainingSeconds])

  const handleUndo = useCallback(() => {
    onUndo(voiceNoteId)
  }, [voiceNoteId, onUndo])

  const actionCount = actionsExecuted.length
  const actionTypes = actionsExecuted.map((a) => ACTION_LABELS[a.type] || a.type).join(' \u00b7 ')

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed bottom-24 left-4 right-4 z-[9998] mx-auto max-w-md"
    >
      <div
        className="flex items-center gap-3 rounded-xl px-4 py-3"
        style={{
          background: 'var(--color-text, #2C2C2A)',
          boxShadow: 'var(--shadow-elevated)',
        }}
      >
        <CheckCircle size={18} className="shrink-0" style={{ color: 'var(--color-status-success)' }} aria-hidden="true" />

        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium" style={{ color: 'var(--color-text-inverse, #fff)' }}>
            {t.voice.toast.actionsCount.replace('{count}', String(actionCount))}
          </p>
          <p className="truncate text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {actionTypes}
          </p>
        </div>

        <button
          onClick={handleUndo}
          className="flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{
            color: 'var(--color-accent)',
            background: 'rgba(255,255,255,0.1)',
            minHeight: '44px',
            minWidth: '44px',
          }}
          aria-label={t.voice.undo.button}
        >
          <Undo2 size={14} aria-hidden="true" />
          {t.voice.undo.button}
        </button>

        <span
          className="min-w-[28px] text-right text-sm"
          style={{ color: 'rgba(255,255,255,0.5)', fontVariantNumeric: 'tabular-nums' }}
          aria-label={t.voice.undo.timer}
        >
          {remainingSeconds}s
        </span>
      </div>
    </div>
  )
}

// ── Action labels ──

const ACTION_LABELS: Record<string, string> = {
  create_rdv: 'RDV',
  update_pipeline: 'Pipeline',
  add_note: 'Note',
  update_bien: 'Bien',
}
